import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useToggle } from '@pletly/common';
import { findFirstTwoUsersInIds, getName } from '@pletly/shared';
import { useConnections } from '%context/Connections';
import { usePrivacyState } from './usePrivacyState';

export const useNewPostPrivacy = (defaultSelectedUsersIds?: string[], internalOnly?: boolean) => {
	const { t } = useTranslation();
	const { careRecipients } = useConnections();
	const [isSelectUsersDisplayed, toggleSelectUsersDisplayed] = useToggle();
	const {
		selectedUsersIds,
		setSelectedUsersIds,
		selectedAllStaff,
		setAllStaffSelected,
		selectedTeamIds,
		setSelectedTeamsIds,
		isInternal,
		setInternalOnly,
	} = usePrivacyState(defaultSelectedUsersIds, internalOnly);
	const hasOneConnectionOnly = Array.isArray(careRecipients) && careRecipients.length === 1;

	useEffect(() => {
		if (!hasOneConnectionOnly || !Array.isArray(careRecipients)) {
			return;
		}

		const [user] = careRecipients;
		setSelectedUsersIds([String(user.id)]);
	}, [hasOneConnectionOnly, careRecipients, setSelectedUsersIds]);

	const selectedUsersInfoText = useMemo(() => {
		if (selectedAllStaff) {
			return t('post.new.all-employees');
		}

		if (selectedTeamIds) {
			return t('post.new.team', { count: selectedTeamIds.length });
		}

		const { firstSelectedUser, secondSelectedUser } = findFirstTwoUsersInIds(
			careRecipients,
			selectedUsersIds,
		);
		if (!firstSelectedUser) {
			return;
		}
		const othersLength = selectedUsersIds.length < 2 ? 0 : selectedUsersIds.length - 2;

		return getName(firstSelectedUser)
			.concat(secondSelectedUser ? `, ${getName(secondSelectedUser)}` : '')
			.concat(othersLength ? ` ${t('post.new.and-others', { count: othersLength })}` : '');
	}, [careRecipients, selectedAllStaff, selectedTeamIds, selectedUsersIds, t]);

	const isPrivacyValid =
		selectedUsersIds.length > 0 || selectedAllStaff || !!selectedTeamIds?.length;

	return {
		careRecipients,
		selectedUsersIds,
		setSelectedUsersIds,
		selectedAllStaff,
		setAllStaffSelected,
		selectedTeamIds,
		setSelectedTeamsIds,
		selectedUsersInfoText,
		isSelectUsersDisplayed,
		toggleSelectUsersDisplayed,
		isPrivacyValid,
		isInternal,
		setInternalOnly,
	};
};
